import React, { useMemo } from 'react'
import axios from 'axios'
import { HttpStatus, Redirect } from '@notino/react-toolkit/renderer/fragment/contexts/HttpContext'
import type { ReqContext } from '@notino/react-toolkit/renderer/fragment/FragmentHandler'
import * as Sentry from '@sentry/node'

// hooks
import usePageData from '../hooks/usePageData'

// utils
import { ERROR_PAGE_CODE } from './enums'

// types
import {
	BasePageData,
	BasePageQuery,
	BasePageRouteProps,
	ExtendedContextProps,
	PageContainerQuery,
	PageContainer,
	PageProps,
	MyLocationType,
	PageContainerReqContext,
	PageRedirectResult,
	SetZodQuery,
	PageRedirect
} from '../types/types'
import FragmentStatus from '../atoms/FragmentStatus/FragmentStatus'

const isPageRedirectResult = (value: unknown): value is PageRedirectResult =>
	typeof value === 'object' && value !== null && 'redirect' in value && typeof (value as PageRedirectResult).redirect?.redirectPath === 'string'

const getRedirectStatusCode = (redirect: PageRedirect): 301 | 302 => (redirect.redirectType === 'permanent' ? 301 : 302)

type CreatePageContainerReturnType<TData extends BasePageData, TQuery extends BasePageQuery, TRouteProps = BasePageRouteProps> = {
	identifier: string
	PageComponent: React.ComponentType<PageProps<TData, TQuery, TRouteProps>>
	/**
	 * Function to fetch data on the server.
	 * Should return only the page data (not wrapped in { success: true }).
	 */
	initDefaultData: (
		args: ExtendedContextProps<PageContainerQuery<TQuery> & TRouteProps>,
		context?: PageContainerReqContext
	) => Promise<TData | PageRedirectResult>
}

/**
 * Factory function for creating strongly-typed page containers with consistent error handling and query sanitization.
 *
 * This function wraps your page component and its server-side data-fetching logic, ensuring:
 * - All page containers have a unified structure and error handling.
 * - The query prop passed to the page component omits user-location keys (latMy, lonMy) to prevent accidental usage.
 * - Errors thrown in initDefaultData are caught and mapped to appropriate error pages (401, 403, 404, etc.).
 * - The identifier and initDefaultData are attached to the container for framework integration.
 *
 * Usage:
 *   export default createPageContainer({
 *     identifier: 'SomePage',
 *     PageComponent: SomePage,
 *     initDefaultData: async (args) => { ... }
 *   })
 *
 * @template TData      The shape of the page data.
 * @template TQuery     The shape of the query parameters.
 * @template TRouteProps The shape of additional route props.
 */

const createPageContainer = <
	TData extends BasePageData,
	TQuery extends BasePageQuery | (BasePageQuery & Partial<MyLocationType>),
	TRouteProps = BasePageRouteProps
>({
	identifier,
	PageComponent,
	initDefaultData
}: CreatePageContainerReturnType<TData, TQuery, TRouteProps>) => {
	const Container: PageContainer<TData, TQuery, TRouteProps> = (props) => {
		const { query, setQuery, ...restProps } = props
		const { pageData, ...usePageDataProps } = usePageData<TData, TQuery, TRouteProps>(Container, props)
		const { retryPageRequest, isFetching } = usePageDataProps

		const pageProps = useMemo(() => {
			/**
			 * Always use values from `MyLocationContext` when working with the user's location, rather than relying on the query parameters.
			 * Although the query includes `latMy` and `lonMy`, these are intended solely for the server to fetch the correct data when the user's detected location is passed via URL query parameters.
			 * To prevent accidental usage of these parameters, this sanitized `query` object excludes user-related location keys.
			 */
			const sanitizedQuery = { ...query }
			if ('latMy' in sanitizedQuery) {
				delete sanitizedQuery.latMy
			}
			if ('lonMy' in sanitizedQuery) {
				delete sanitizedQuery.lonMy
			}

			/**
			 * Function to set the query parameters, excluding user location keys.
			 */
			const sanitizedSetQuery: SetZodQuery<TQuery> = (newQueryOrUpdater, ...rest) => {
				setQuery(
					(currentQuery) => {
						const resolvedNewQuery: Partial<TQuery> & Partial<MyLocationType> =
							typeof newQueryOrUpdater === 'function' ? newQueryOrUpdater(currentQuery) : newQueryOrUpdater

						resolvedNewQuery.latMy = undefined
						resolvedNewQuery.lonMy = undefined

						return resolvedNewQuery
					},
					...rest
				)
			}

			return {
				...pageData,
				...usePageDataProps,
				...restProps,
				query: sanitizedQuery,
				setQuery: sanitizedSetQuery
			} as PageProps<TData, TQuery, TRouteProps>
		}, [pageData, usePageDataProps, restProps, query, setQuery])

		if (!pageData) {
			return null
		}

		if (pageData.success === 'redirect') {
			return (
				<HttpStatus status={getRedirectStatusCode(pageData.redirect)}>
					<Redirect to={pageData.redirect.redirectPath} />
				</HttpStatus>
			)
		}

		if (pageData.success === false) {
			return (
				<FragmentStatus
					statusCode={pageData.statusCodeToRender || ERROR_PAGE_CODE.GENERAL_ERROR}
					onRetry={retryPageRequest}
					isRetrying={isFetching}
					{...props}
				/>
			)
		}

		return <PageComponent {...pageProps} />
	}

	Container.initDefaultData = async (args, context?: ReqContext) => {
		try {
			const data = await initDefaultData(args, context)

			if (isPageRedirectResult(data)) {
				return { success: 'redirect', ...data }
			}

			return { success: true, ...data }
		} catch (error) {
			let statusCodeToRender: ERROR_PAGE_CODE = ERROR_PAGE_CODE.GENERAL_ERROR

			if (axios.isAxiosError(error)) {
				switch (error.response?.status) {
					/**
					 * The Notino layout service currently handles 401 and 403 errors as 404 pages.
					 * However, this logic is prepared to accommodate potential future changes.
					 */
					case 404:
						statusCodeToRender = ERROR_PAGE_CODE.NOT_FOUND
						break
					case 401:
						statusCodeToRender = ERROR_PAGE_CODE.UNAUTHORIZED
						break
					case 403:
						statusCodeToRender = ERROR_PAGE_CODE.FORBIDDEN
						break
					default:
						break
				}
			} else {
				Sentry.captureException(error, { extra: { identifier: 'Container.initDefaultData' } })
			}
			return { success: false, statusCodeToRender }
		}
	}

	Container.identifier = identifier

	return Container
}

export default createPageContainer
